import { useCallback, useEffect, useState } from 'react';
import type { ViewportTier } from './useViewportTier';

export type WbsColumnKey =
  | 'name'
  | 'system'
  | 'backlogProject'
  | 'assignee'
  | 'plannedStart'
  | 'plannedEnd'
  | 'status';

export const WBS_COLUMN_DEFAULTS: Record<WbsColumnKey, number> = {
  name: 280,
  system: 88,
  backlogProject: 88,
  assignee: 92,
  plannedStart: 96,
  plannedEnd: 96,
  status: 84,
};

export const WBS_COLUMN_LIMITS: Record<
  WbsColumnKey,
  { min: number; max: number }
> = {
  name: { min: 140, max: 640 },
  system: { min: 48, max: 360 },
  backlogProject: { min: 48, max: 360 },
  assignee: { min: 48, max: 360 },
  plannedStart: { min: 72, max: 200 },
  plannedEnd: { min: 72, max: 200 },
  status: { min: 56, max: 200 },
};

/** px of the drag-handle column that precedes every data column. */
export const WBS_HANDLE_WIDTH = 24;

const STORAGE_KEY = 'mct.wbs.columnWidths.v9';

function readStoredWidths(): Record<WbsColumnKey, number> {
  if (typeof window === 'undefined') return { ...WBS_COLUMN_DEFAULTS };
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...WBS_COLUMN_DEFAULTS };
    const parsed = JSON.parse(raw) as Partial<Record<WbsColumnKey, number>>;
    const merged = { ...WBS_COLUMN_DEFAULTS };
    for (const k of Object.keys(WBS_COLUMN_DEFAULTS) as WbsColumnKey[]) {
      const v = parsed[k];
      const { min, max } = WBS_COLUMN_LIMITS[k];
      if (typeof v === 'number' && v >= min && v <= max) {
        merged[k] = Math.round(v);
      }
    }
    return merged;
  } catch {
    return { ...WBS_COLUMN_DEFAULTS };
  }
}

export interface WbsColumnWidthsApi {
  widths: Record<WbsColumnKey, number>;
  totalWidth: number;
  gridTemplate: string;
  beginResize: (key: WbsColumnKey) => (e: React.MouseEvent) => void;
}

/**
 * Shared hook so WbsPage can size the outer sticky-tree container in
 * lockstep with WbsTreeTable's internal grid. Persists to localStorage
 * on every change.
 *
 * The optional `tier` argument adapts the emitted `gridTemplate` to the
 * Phase 1 responsive layout:
 *
 *   - 'mobile'  : `name | status` (2 columns, name flex-1)
 *   - 'tablet'  : `name | assignee | plannedEnd | status` (4)
 *   - 'desktop' : 8-column layout (handle, name, system, backlogProject,
 *                 assignee, plannedStart, plannedEnd, status)
 *
 * The hidden cells in WbsTreeTable carry matching `hidden / sm:flex /
 * lg:flex` Tailwind classes, so cells that don't fit the active tier
 * collapse out of the document flow and the visible cells flow into the
 * tier's grid columns in DOM order.
 */
export function useWbsColumnWidths(
  tier: ViewportTier = 'desktop',
): WbsColumnWidthsApi {
  const [widths, setWidths] = useState<Record<WbsColumnKey, number>>(
    readStoredWidths,
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(widths));
    } catch {
      /* ignore quota / privacy-mode errors */
    }
  }, [widths]);

  const beginResize = useCallback(
    (key: WbsColumnKey) => (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const startX = e.clientX;
      const startW = widths[key];
      const { min, max } = WBS_COLUMN_LIMITS[key];
      const onMove = (ev: MouseEvent) => {
        const next = Math.max(min, Math.min(max, startW + (ev.clientX - startX)));
        setWidths((prev) =>
          prev[key] === Math.round(next)
            ? prev
            : { ...prev, [key]: Math.round(next) },
        );
      };
      const onUp = () => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    },
    [widths],
  );

  // Clamp to current limits at render time so a stale localStorage value
  // (or React state preserved across HMR) can never overflow the column
  // bounds — UI always reflects the current WBS_COLUMN_LIMITS.
  const clamp = (k: WbsColumnKey) => {
    const { min, max } = WBS_COLUMN_LIMITS[k];
    return Math.max(min, Math.min(max, widths[k]));
  };

  // Mobile / tablet emit a flex-name template so the table fills the
  // viewport and doesn't try to scroll horizontally — the Gantt area is
  // hidden on `< lg` and the detail panel becomes a full-screen overlay,
  // so the tree owns the entire width.
  if (tier === 'mobile') {
    const gridTemplate = [
      'minmax(0,1fr)',
      `${clamp('status')}px`,
    ].join(' ');
    return { widths, totalWidth: 0, gridTemplate, beginResize };
  }
  if (tier === 'tablet') {
    const gridTemplate = [
      'minmax(0,1fr)',
      `${clamp('assignee')}px`,
      `${clamp('plannedEnd')}px`,
      `${clamp('status')}px`,
    ].join(' ');
    return { widths, totalWidth: 0, gridTemplate, beginResize };
  }

  const totalWidth =
    WBS_HANDLE_WIDTH +
    clamp('name') +
    clamp('system') +
    clamp('backlogProject') +
    clamp('assignee') +
    clamp('plannedStart') +
    clamp('plannedEnd') +
    clamp('status');

  const gridTemplate = [
    `${WBS_HANDLE_WIDTH}px`,
    `${clamp('name')}px`,
    `${clamp('system')}px`,
    `${clamp('backlogProject')}px`,
    `${clamp('assignee')}px`,
    `${clamp('plannedStart')}px`,
    `${clamp('plannedEnd')}px`,
    `${clamp('status')}px`,
  ].join(' ');

  return { widths, totalWidth, gridTemplate, beginResize };
}
